import React, { useEffect, useState, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/dateFormat";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Send } from "lucide-react";

export default function MessagesPanel() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/users/municipality-contacts").then((r) => {
      setContacts(r.data);
      if (r.data.length && !active) setActive(r.data[0]);
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    const load = () => api.get(`/messages?with_user=${active.id}`).then((r) => setMsgs(r.data));
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [active]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    await api.post("/messages", { to_user_id: active.id, body: text.trim() });
    setText("");
    const r = await api.get(`/messages?with_user=${active.id}`);
    setMsgs(r.data);
  };

  return (
    <Card className="border-border">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 sm:grid-cols-3" style={{ minHeight: 400 }}>
          <div className="border-b border-border sm:border-b-0 sm:border-r">
            <div className="label-caps px-4 pt-4">{t("contact")}</div>
            <ScrollArea className="max-h-80">
              <ul className="p-2">
                {contacts.length === 0 && (
                  <li className="p-3 text-sm text-muted-foreground">{t("no_data")}</li>
                )}
                {contacts.map((c) => (
                  <li key={c.id}>
                    <button
                      className={`w-full rounded-md px-3 py-2 text-left text-sm hover:bg-muted ${
                        active?.id === c.id ? "bg-muted font-semibold" : ""
                      }`}
                      onClick={() => setActive(c)}
                      data-testid={`msg-contact-${c.id}`}
                    >
                      <div className="truncate">{c.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{c.email}</div>
                    </button>
                  </li>
                ))}
              </ul>
            </ScrollArea>
          </div>

          <div className="sm:col-span-2 flex flex-col">
            <div className="flex-1 overflow-hidden">
              <ScrollArea className="h-80 p-4">
                {msgs.length === 0 && (
                  <div className="grid h-full place-items-center text-sm text-muted-foreground">
                    {t("no_messages")}
                  </div>
                )}
                <div className="space-y-2">
                  {msgs.map((m) => {
                    const mine = m.from_user_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[80%] rounded-md border px-3 py-2 text-sm ${
                            mine
                              ? "border-primary/30 bg-primary/10"
                              : "border-border bg-card"
                          }`}
                        >
                          <div className="whitespace-pre-wrap">{m.body}</div>
                          <div className="mt-1 text-[10px] text-muted-foreground">
                            <span title={m.created_at}>{formatDateTime(m.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
              </ScrollArea>
            </div>
            <form onSubmit={send} className="flex gap-2 border-t border-border p-3">
              <Input
                placeholder={t("write_message")}
                value={text}
                onChange={(e) => setText(e.target.value)}
                data-testid="msg-input"
                disabled={!active}
              />
              <Button type="submit" disabled={!active || !text.trim()} data-testid="msg-send-btn">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
